import {
  AbsoluteCenter,
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Input,
} from '@chakra-ui/react';
import * as React from 'react';

import { EPrepImagesSource } from '@/graphql-types/globalTypes';
import { useDraftResources } from '@/hooks/useDraftResources';
import { useImageUpload } from '@/hooks/useImageUpload';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { ImageIcon } from '@/imports/ui/chakra/feather';

export const BlogImageEdit = React.forwardRef<
  HTMLInputElement,
  {
    value: { imageId: string };
    onChange: (value: { imageId: string } | null) => void;
    onBlur: () => void;
    originalImage?: { imageId: string; imageProxy: string };
  }
>(({ value, onChange, onBlur, originalImage }, ref) => {
  const originalDescriptiveResources = React.useMemo(
    () => (originalImage ? [originalImage] : []),
    [originalImage]
  );
  const newDescriptiveResourcesIds = React.useMemo(
    () => (value ? [{ imageId: value.imageId }] : []),
    [value]
  );
  const imageInput = React.useMemo(() => {
    return { ar: '8:3', w: 1600, fit: 'fill', fill: 'blur' };
  }, []);

  const { resourcesSyncedWithDraft } = useDraftResources({
    originalDescriptiveResources: originalDescriptiveResources,
    newDescriptiveResourcesIds: newDescriptiveResourcesIds,
    source: EPrepImagesSource.BLOG,
    imageInput: imageInput,
  });

  const internalRef = React.useRef<HTMLInputElement | null>(null);
  React.useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

  const { uploadImages } = useImageUpload(EPrepImagesSource.BLOG);

  const [isLoading, setIsLoading] = React.useState(false);

  return (
    <Box>
      <Input
        type="file"
        multiple={false}
        onChange={async (event) => {
          setIsLoading(true);
          const files = event.currentTarget.files
            ? Array.from(event.currentTarget.files)
            : [];
          const promisedResources = files.map(async (file) => {
            const resource = await uploadImages([file]);

            return resource ? { imageId: resource[0] } : null;
          });
          const resources = (await Promise.all(promisedResources)).filter(
            (resource): resource is { imageId: string } => !!resource
          );

          setIsLoading(false);
          return onChange && onChange(resources[0]);
        }}
        onBlur={onBlur}
        ref={internalRef}
        display="none"
      />
      {isLoading ? (
        <Loading aspectRatio="8/3" bg="gray.100" />
      ) : !resourcesSyncedWithDraft[0]?.url ? (
        <Flex
          position="relative"
          sx={{ aspectRatio: '8/3' }}
          bg="gray.100"
          alignItems="end"
          justifyContent="end"
          p="2"
        >
          <AbsoluteCenter>
            <ImageIcon color="gray.400" />
          </AbsoluteCenter>
          <Button
            variant="outline"
            colorScheme="gray"
            onClick={() => internalRef.current?.click()}
          >
            Add Cover Photo
          </Button>
        </Flex>
      ) : (
        <Flex
          sx={{ aspectRatio: '8/3' }}
          position="relative"
          alignItems="end"
          justifyContent="end"
          p="2"
        >
          <AbsoluteCenter w="full">
            <Image
              aria-label="Upload Files"
              src={resourcesSyncedWithDraft[0]?.url}
              fallback={
                <ImageFallback
                  aspectRatio="8/3"
                  _hover={{
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    internalRef.current?.click();
                  }}
                />
              }
            />
          </AbsoluteCenter>

          <HStack>
            <Button
              aria-label="Remove cover photo"
              variant="outline"
              colorScheme="gray"
              onClick={() => onChange(null)}
            >
              Remove
            </Button>
            <Button
              aria-label="Change cover photo"
              variant="outline"
              colorScheme="gray"
              onClick={() => internalRef.current?.click()}
            >
              Change Cover Photo
            </Button>
          </HStack>
        </Flex>
      )}
    </Box>
  );
});

BlogImageEdit.displayName = 'BlogImageEdit';
